import { addDays, DateOnly } from '@if/core/date'
import { EFinancingProgram, EProvince } from '@if/core/types'
import {
  ControlledCardCheckbox,
  ControlledDateOnlyPicker,
  ControlledSelect,
  FormContainer,
  useFormContainerContext,
} from '@if/mui'
import { Alert, Divider, Grid, MenuItem, Paper, Stack, Typography } from '@mui/material'
import { Breadcrumb, PageError, PageSpinner } from '@src/components'
import BaseWorksheetForm, {
  BaseWorksheetFormConfig,
  useBaseWorksheetFormConfigFromCreditApp,
} from '@src/components/WorksheetForm'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import {
  useComputeEasyCare,
  useEasyCareParentLoanSchedule,
  useUpdateEasyCareWorksheet,
} from '@src/data/api/easycare-worksheet/easycare-worksheet-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { CreditApplication } from '@src/data/types/CreditApplicationSchema'
import { EasyCareParentLoanScheduleDto, EasyCareWorksheet } from '@src/data/types/EasyCareWorksheetSchema'
import { Merchant } from '@src/data/types/Merchant'
import { ReactElement, useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import EasyCareFundingSummary from './Components/EasyCareFundingSummary'
import {
  EasyCareEditSubmitValues,
  EasyCareEditValues,
  toEditEasyCareWorksheetDto,
  useEasyCareEditForm,
  VEHICLE_ASSISTANCE_TERMS,
} from './easyCareEditForm'

const EditEasyCareWorksheetPage = () => {
  const { creditApplication, worksheet, isLoading } = useCreditApplicationData()
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')

  if (isLoading || !creditApplication || !merchant || !worksheet) {
    return <PageSpinner isLoading />
  }

  return (
    <PageReady creditApplication={creditApplication} merchant={merchant} worksheet={worksheet as EasyCareWorksheet} />
  )
}

interface PageReadyProps {
  creditApplication: CreditApplication
  merchant: Merchant
  worksheet: EasyCareWorksheet
}

const PageReady = ({ creditApplication, merchant, worksheet }: PageReadyProps) => {
  const navigate = useNavigate()
  const { config, isLoading: isConfigLoading } = useBaseWorksheetFormConfigFromCreditApp({
    creditApplication,
    merchant,
  })

  const { schema, defaultValues, isReady } = useEasyCareEditForm(config, worksheet)
  const [parentLoanSchedule, isScheduleLoading] = useEasyCareParentLoanSchedule(creditApplication.id)

  const seededDefaultValues = useMemo<EasyCareEditValues | undefined>(() => {
    if (!defaultValues || !parentLoanSchedule?.isActive) return defaultValues
    return {
      ...defaultValues,
      activationDate: new DateOnly(parentLoanSchedule.activationDate),
      firstPaymentDate: new DateOnly(parentLoanSchedule.firstPaymentDate),
      paymentFrequency: parentLoanSchedule.paymentFrequency ?? defaultValues.paymentFrequency,
    }
  }, [defaultValues, parentLoanSchedule])

  const [updateWorksheet, isUpdating] = useUpdateEasyCareWorksheet()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()

  const onFormSubmitted = async (values: EasyCareEditSubmitValues) => {
    await updateWorksheet(toEditEasyCareWorksheetDto(values, worksheet))
    await invalidateFullCreditApplication(creditApplication.id, creditApplication.financingProgramId)
    navigate(`/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${creditApplication.id}/view`, {
      replace: true,
    })
  }

  if (isConfigLoading || isScheduleLoading || !isReady || !seededDefaultValues || !parentLoanSchedule) {
    return <PageSpinner isLoading />
  }

  return (
    <div>
      <PageSpinner withBackdrop isLoading={isUpdating} />
      <FormContainer
        useFormSetup={() => ({ schema, defaultValues: seededDefaultValues })}
        mode="edit"
        onSubmit={(values) => onFormSubmitted(values)}
      >
        <PageContent
          creditApplication={creditApplication}
          config={config}
          parentLoanSchedule={parentLoanSchedule}
        />
      </FormContainer>
    </div>
  )
}

interface PageContentProps {
  creditApplication: CreditApplication
  config: BaseWorksheetFormConfig
  parentLoanSchedule: EasyCareParentLoanScheduleDto
}

const PageContent = ({ creditApplication, config, parentLoanSchedule }: PageContentProps) => {
  const { t } = useTranslation()
  const error = useBusinessErrors()
  const { methods } = useFormContainerContext<EasyCareEditValues>()

  const activationMaxDate = useMemo(() => new DateOnly(config.expiresOn), [config.expiresOn])

  const vehicleAssistanceTermOptions = useMemo<ReactElement[]>(
    () =>
      VEHICLE_ASSISTANCE_TERMS.map((term) => (
        <MenuItem key={term} value={term}>
          {t('easyCare.months', { count: term })}
        </MenuItem>
      )),
    [t],
  )

  const supportsVehicleAssistance = creditApplication.applicant.currentAddress.stateIso !== EProvince.Quebec
  const isParentLoanActive = parentLoanSchedule.isActive

  const earliestFirstPaymentDate = useMemo(() => new DateOnly(addDays(DateOnly.today(), 8)), [])

  const [
    includeVehicleAssistance,
    includeLoanProtection,
    vehicleAssistanceTerm,
    paymentFrequency,
    activationDate,
    firstPaymentDate,
  ] = useWatch({
    control: methods.control,
    name: [
      'includeVehicleAssistance',
      'includeLoanProtection',
      'vehicleAssistanceTerm',
      'paymentFrequency',
      'activationDate',
      'firstPaymentDate',
    ],
  })

  useEffect(() => {
    if (!supportsVehicleAssistance && includeVehicleAssistance) {
      methods.setValue('includeVehicleAssistance', false, { shouldDirty: true })
    }
  }, [supportsVehicleAssistance, includeVehicleAssistance, methods])

  useEffect(() => {
    if (!isParentLoanActive && includeLoanProtection) {
      methods.setValue('includeLoanProtection', false, { shouldDirty: true })
    }
  }, [isParentLoanActive, includeLoanProtection, methods])

  const computeDto = useMemo(() => {
    if (!activationDate || !firstPaymentDate) return undefined
    if (includeVehicleAssistance && !vehicleAssistanceTerm) return undefined
    return {
      creditApplicationId: creditApplication.id,
      includeVehicleAssistance,
      includeLoanProtection,
      vehicleAssistanceTerm: vehicleAssistanceTerm ?? 0,
      paymentFrequency,
      deliveryOn: activationDate.toString(),
      firstPaymentDate: firstPaymentDate.toString(),
    }
  }, [
    creditApplication.id,
    includeVehicleAssistance,
    includeLoanProtection,
    vehicleAssistanceTerm,
    paymentFrequency,
    activationDate,
    firstPaymentDate,
  ])

  const [computedInfo, isComputing, computeFailed] = useComputeEasyCare(computeDto)

  const noProductSelected = !includeVehicleAssistance && !includeLoanProtection

  useEffect(() => {
    methods.setValue('amountRequested', computedInfo?.totalAmountFinanced ?? 0)
    methods.setValue('term', computedInfo?.term ?? 0)
  }, [computedInfo, methods])

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${creditApplication.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  return (
    <Stack spacing={2}>
      <PageError errors={error} />
      <Breadcrumb trees={breadCrumbs} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <BaseWorksheetForm config={config}>
              <Grid size={12}>
                <Typography variant="h5">{t('easyCare.products')}</Typography>
              </Grid>

              {!supportsVehicleAssistance && (
                <Grid size={12}>
                  <Alert severity="info">{t('easyCare.vehicleAssistanceUnavailableInQuebec')}</Alert>
                </Grid>
              )}

              {supportsVehicleAssistance && (
                <Grid size={12}>
                  <ControlledCardCheckbox
                    control={methods.control}
                    name="includeVehicleAssistance"
                    label={t('easyCare.vehicleAssistanceStatement')}
                  />
                </Grid>
              )}

              {supportsVehicleAssistance && includeVehicleAssistance && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <ControlledSelect
                    control={methods.control}
                    name="vehicleAssistanceTerm"
                    label={t('easyCare.vehicleAssistanceTerm')}
                    options={vehicleAssistanceTermOptions}
                    disableEmptyOption
                    fullWidth
                  />
                </Grid>
              )}

              <Grid size={12}>
                <ControlledCardCheckbox
                  control={methods.control}
                  name="includeLoanProtection"
                  label={t('easyCare.loanProtectionStatement')}
                  disabled={!isParentLoanActive}
                />
              </Grid>

              {!isParentLoanActive && (
                <Grid size={12}>
                  <Alert severity="info">{t('easyCare.loanProtectionRequiresAnActiveLoan')}</Alert>
                </Grid>
              )}

              {noProductSelected && (
                <Grid size={12}>
                  <Alert severity="warning">{t('easyCare.selectAProduct')}</Alert>
                </Grid>
              )}

              {computeFailed && (
                <Grid size={12}>
                  <Alert severity="error">{t('easyCare.computeFailed')}</Alert>
                </Grid>
              )}

              <Grid size={12}>
                <Divider />
              </Grid>

              <Grid size={12}>
                <Typography variant="h5">{t('worksheet.finance')}</Typography>
              </Grid>

              {isParentLoanActive ? (
                <>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <ControlledDateOnlyPicker
                      name="activationDate"
                      control={methods.control}
                      label={t('worksheetCommon.activationDate')}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <ControlledDateOnlyPicker
                      name="firstPaymentDate"
                      control={methods.control}
                      label={t('worksheetCommon.firstPaymentDate')}
                      disabled
                      fullWidth
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <BaseWorksheetForm.ActivationDate control={methods.control} maxDate={activationMaxDate} />
                  <BaseWorksheetForm.FirstPaymentDate
                    control={methods.control}
                    minDate={earliestFirstPaymentDate}
                  />
                </>
              )}
              <BaseWorksheetForm.PaymentFrequency control={methods.control} locked={isParentLoanActive} />

              <Grid size={12}>
                <Alert severity="info">
                  {isParentLoanActive ? t('easyCare.scheduleFollowsTheParentLoan') : t('easyCare.loanTermIsDerived')}
                </Alert>
              </Grid>
            </BaseWorksheetForm>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <EasyCareFundingSummary
            computedInfo={computedInfo}
            includeVehicleAssistance={includeVehicleAssistance}
            includeLoanProtection={includeLoanProtection}
            paymentFrequency={paymentFrequency}
            firstPaymentDate={firstPaymentDate}
            isFetching={isComputing}
          />
        </Grid>
      </Grid>
    </Stack>
  )
}

export default EditEasyCareWorksheetPage
